import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import type { NavEntry } from './navConfig';

export function NavEmoji({ emoji }: { emoji: string }) {
  return (
    <span className="text-base leading-none select-none" aria-hidden>
      {emoji}
    </span>
  );
}

const topLinkClass = (active: boolean) =>
  `inline-flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
    active
      ? 'border-blue-600 text-blue-600'
      : 'border-transparent text-neutral-600 hover:text-neutral-900 hover:border-neutral-300'
  }`;

export function NavTopLink({
  item,
  active,
  onClick,
}: {
  item: NavEntry;
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <Link to={item.href} onClick={onClick} className={topLinkClass(active)}>
      <NavEmoji emoji={item.icon} />
      {item.label}
    </Link>
  );
}

export function NavDropdownTrigger({
  item,
  active,
  open,
  onClick,
}: {
  item: NavEntry;
  active: boolean;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className={topLinkClass(active)}>
      <NavEmoji emoji={item.icon} />
      {item.label}
      <ChevronDown
        className={`h-3.5 w-3.5 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
        strokeWidth={2}
      />
    </button>
  );
}

export function NavDropdownLink({
  item,
  active,
  onClick,
  trailing,
}: {
  item: NavEntry;
  active: boolean;
  onClick?: () => void;
  trailing?: React.ReactNode;
}) {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
        active
          ? 'bg-blue-50 text-blue-700 font-medium'
          : 'text-neutral-700 hover:bg-neutral-50'
      }`}
    >
      <NavEmoji emoji={item.icon} />
      <span className="flex-1 truncate">{item.label}</span>
      {trailing}
    </Link>
  );
}

export function NavMobileLink({
  item,
  active,
  onClick,
  trailing,
  indent = false,
}: {
  item: NavEntry;
  active: boolean;
  onClick?: () => void;
  trailing?: React.ReactNode;
  indent?: boolean;
}) {
  return (
    <Link to={item.href} onClick={onClick}>
      <div
        className={`flex items-center gap-2 rounded-md py-2 text-sm font-medium transition-colors ${
          indent ? 'pl-6 pr-4' : 'px-3'
        } ${
          active
            ? 'bg-blue-50 text-blue-700'
            : 'text-neutral-700 hover:bg-neutral-50'
        }`}
      >
        <NavEmoji emoji={item.icon} />
        <span className="flex-1">{item.label}</span>
        {trailing}
      </div>
    </Link>
  );
}

export function HeaderEmojiButton({
  emoji,
  label,
  unreadCount,
  secondaryUnreadCount,
  onClick,
}: {
  emoji: string;
  label: string;
  unreadCount?: number;
  secondaryUnreadCount?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative px-1 py-3 text-neutral-600 transition-colors hover:text-neutral-900"
      title={label}
      aria-label={label}
    >
      <span className="text-lg leading-none">{emoji}</span>
      {unreadCount != null && unreadCount > 0 && (
        <span className="absolute -right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
      {secondaryUnreadCount != null && secondaryUnreadCount > 0 && (
        <span
          className={`absolute flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-amber-500 px-0.5 text-[9px] font-semibold text-white ${
            unreadCount != null && unreadCount > 0 ? '-left-0.5 bottom-1' : '-right-1 top-1 h-4 min-w-[16px] text-[10px] px-1'
          }`}
        >
          {secondaryUnreadCount > 99 ? '99+' : secondaryUnreadCount}
        </span>
      )}
    </button>
  );
}

export function MobileMenuToggle({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="rounded-md p-2 text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 md:hidden"
      onClick={onClick}
      aria-label={open ? 'Close menu' : 'Open menu'}
    >
      {open ? <X className="h-5 w-5" strokeWidth={1.75} /> : <Menu className="h-5 w-5" strokeWidth={1.75} />}
    </button>
  );
}
